import React, { useState, useEffect } from 'react';
import { cvApi } from '../services/api';
import CvUploadForm from '../components/CvUploadForm';
import CvList from '../components/CvList';
import './CvManagement.css';

function CvManagement({ currentUser }) {
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchCvs();
  }, []);

  const fetchCvs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await cvApi.getMyCvs();
      setCvs(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching CVs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      const result = await cvApi.upload(file);
      setSuccessMessage(`CV "${result.fileName}" uploaded successfully!`);

      // Refresh the CV list
      await fetchCvs();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err.message);
      console.error('Error uploading CV:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (cv) => {
    try {
      const blob = await cvApi.download(cv.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cv.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
      console.error('Error downloading CV:', err);
    }
  };

  const handleDelete = async (cvId) => {
    if (!window.confirm('Are you sure you want to delete this CV?')) {
      return;
    }

    try {
      await cvApi.delete(cvId);
      setSuccessMessage('CV deleted successfully');

      // Refresh the CV list
      await fetchCvs();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err.message);
      console.error('Error deleting CV:', err);
    }
  };

  return (
    <div className="cv-management-container">
      <div className="cv-management-header">
        <h1>CV Management</h1>
        <p>Upload and manage your curriculum vitae documents</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">&times;</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="alert-close">&times;</button>
        </div>
      )}

      <div className="cv-management-content">
        <CvUploadForm
          onUpload={handleUpload}
          uploading={uploading}
        />

        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading your CVs...</p>
          </div>
        ) : (
          <CvList
            cvs={cvs}
            onDownload={handleDownload}
            onDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
}

export default CvManagement;
